import { mapPaginatedResponse, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  CarrierEnablementScope,
  CreateVehicle,
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
  SortParams,
  UpdateVehicle,
  User,
  Vehicle,
  VehicleStatus,
  VehicleWithResolvedRefs,
} from '@klareospace/pharos-lib-types';

import type { VehicleSortableColumn } from '../types/vehicle.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchVehiclesParams {
  allowedDefaultDriverId?: string;
  carrierEnablementScope?: CarrierEnablementScope;
  carrierId?: string;
  hasNoDefaultDriver?: boolean;
  ids?: string[];
  page?: number;
  pageSize?: number;
  search?: string;
  siteId?: string;
  siteIds?: string[];
  sort?: SortParams<VehicleSortableColumn>;
  spaceId?: string;
  statuses?: VehicleStatus[];
  vehicleTemplateId?: string;
}

export interface AvailableDriversParams {
  carrierId?: string;
  excludeVehicleId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  siteIds?: string[];
  spaceId: string;
}

// ─── Result Types ─────────────────────────────────────────────────────────────

export type VehicleSearchResult = PaginatedResponse<VehicleWithResolvedRefs>;

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createVehicleApi(client: ApiClient): {
  archiveVehicle: (id: string) => Promise<Vehicle>;
  createVehicle: (body: CreateVehicle) => Promise<Vehicle>;
  getAvailableDrivers: (params: AvailableDriversParams) => Promise<PaginatedResponse<User>>;
  getVehicle: (id: string) => Promise<Vehicle>;
  searchVehicles: (params: SearchVehiclesParams) => Promise<VehicleSearchResult>;
  updateVehicle: (id: string, body: UpdateVehicle) => Promise<Vehicle>;
} {
  return {
    archiveVehicle: async (id: string): Promise<Vehicle> => {
      const response = await client.execute<PharosApiResponse<Vehicle>>(() =>
        client.instance.delete(`/vehicles/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    createVehicle: async (body: CreateVehicle): Promise<Vehicle> => {
      const response = await client.execute<PharosApiResponse<Vehicle>>(() => client.instance.post('/vehicles', body));
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    getAvailableDrivers: async (params: AvailableDriversParams): Promise<PaginatedResponse<User>> => {
      const body = toAvailableDriversBody(params);
      const query: Record<string, number | undefined> = {
        limit: params.pageSize,
        page: params.page,
      };
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<User>>>(() =>
        client.instance.post('/drivers/available-for-default-vehicle', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },

    getVehicle: async (id: string): Promise<Vehicle> => {
      const response = await client.execute<PharosApiResponse<Vehicle>>(() => client.instance.get(`/vehicles/${id}`));
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchVehicles: async (params: SearchVehiclesParams): Promise<VehicleSearchResult> => {
      const body = toSearchBody(params);
      const query: Record<string, number | string | undefined> = {
        limit: params.pageSize,
        page: params.page,
      };
      if (params.sort) {
        query.sort = params.sort.sortBy;
        query.sortDirection = params.sort.sortDirection ?? undefined;
      }
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<VehicleWithResolvedRefs>>>(() =>
        client.instance.post('/vehicles/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },

    updateVehicle: async (id: string, body: UpdateVehicle): Promise<Vehicle> => {
      const response = await client.execute<PharosApiResponse<Vehicle>>(() =>
        client.instance.patch(`/vehicles/${id}`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchVehiclesParams): Record<string, unknown> {
  const candidates: Record<string, unknown> = {
    allowedDefaultDriverId: params.allowedDefaultDriverId,
    carrierEnablementScope: params.carrierEnablementScope,
    carrierId: params.carrierId,
    hasNoDefaultDriver: params.hasNoDefaultDriver ? true : undefined,
    ids: params.ids?.length ? params.ids : undefined,
    search: params.search?.trim(),
    siteId: params.siteId,
    siteIds: params.siteIds?.length ? params.siteIds : undefined,
    spaceId: params.spaceId,
    statuses: params.statuses?.length ? params.statuses : undefined,
    vehicleTemplateId: params.vehicleTemplateId,
  };
  return pruneEmpty(candidates);
}

function toAvailableDriversBody(params: AvailableDriversParams): Record<string, unknown> {
  const trimmedSearch = params.search?.trim();
  const candidates: Record<string, unknown> = {
    carrierId: params.carrierId,
    excludeVehicleId: params.excludeVehicleId,
    search: trimmedSearch || undefined,
    siteIds: params.siteIds?.length ? params.siteIds : undefined,
    spaceId: params.spaceId,
  };
  return pruneEmpty(candidates);
}

// Keeps only entries with a meaningful value (drops undefined/null/empty-string).
function pruneEmpty(candidates: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(candidates)) {
    if (value !== undefined && value !== null && value !== '') {
      body[key] = value;
    }
  }
  return body;
}
